import React, { useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";

export default function AuthPage() {
  const { login, register, isAuthenticated } = useAuth();
  const { hero } = useCatalog();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (location.state && location.state.from && location.state.from.pathname) || "/";

  const [mode, setMode] = useState(
    searchParams.get("mode") === "register" ? "register" : "login"
  );
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) {
    navigate(from, { replace: true });
  }

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email.trim(), form.password);
      } else {
        if (form.name.trim().length < 2) throw new Error("Please enter your name");
        if (form.password.length < 6) throw new Error("Password must be at least 6 characters");
        await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          phone: form.phone.trim(),
        });
      }
      navigate(from, { replace: true });
    } catch (err) {
      const detail =
        (err && err.response && err.response.data && err.response.data.detail) ||
        err.message ||
        "Something went wrong";
      setError(String(detail));
    } finally {
      setBusy(false);
    }
  };

  const gradFrom = hero?.loginGradientFrom || "#2B1810";
  const gradTo = hero?.loginGradientTo || "#8B5A2B";
  const loginTitle = hero?.loginTitle || "Welcome to Alankar.";
  const loginSubtitle =
    hero?.loginSubtitle ||
    "Sign in to explore over 1,000 designs, save your favourites and get a personalised WhatsApp enquiry with one tap.";
  const loginTagline = hero?.loginTagline || "30+ Years of Trust · Rasta Peth, Pune";
  const loginLogo = hero?.loginLogoImage || "";

  return (
    <div
      className="auth-page"
      style={{
        "--auth-grad-from": gradFrom,
        "--auth-grad-to": gradTo,
      }}
    >
      <div className="auth-side">
        <div className="auth-side-inner">
          <Link to="/" className="auth-brand" data-testid="auth-brand">
            {loginLogo ? (
              <img
                src={loginLogo}
                alt="Alankar Jewellers"
                className="auth-logo-img"
                data-testid="auth-brand-logo"
              />
            ) : (
              <>
                <span className="name">Alankar Jewellers</span>
                <span className="sub">RASTA PETH · PUNE</span>
              </>
            )}
          </Link>
          <h1 data-testid="auth-title">{loginTitle}</h1>
          <p data-testid="auth-subtitle">{loginSubtitle}</p>
          <ul className="auth-perks">
            <li>✦ Personal Wishlist across visits</li>
            <li>✦ One-tap WhatsApp enquiry for any design</li>
            <li>✦ Access to 20+ categories and 1,000+ designs</li>
          </ul>
          <div className="auth-cred" data-testid="auth-tagline">{loginTagline}</div>
        </div>
      </div>

      <div className="auth-form-wrap">
        <div className="auth-form">
          <div className="auth-tabs">
            <button
              type="button"
              className={mode === "login" ? "on" : ""}
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              className={mode === "register" ? "on" : ""}
              onClick={() => {
                setMode("register");
                setError("");
              }}
            >
              Create Account
            </button>
          </div>

          <h2>
            {mode === "login"
              ? "Sign in to continue"
              : "Create your account"}
          </h2>
          <p className="auth-hint">
            {mode === "login"
              ? "Enter your credentials to access the collection."
              : "Register with your email to save designs and enquire faster."}
          </p>

          <form onSubmit={submit}>
            {mode === "register" && (
              <label className="auth-field">
                <span>Full name</span>
                <input
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                />
              </label>
            )}
            <label className="auth-field">
              <span>Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            {mode === "register" && (
              <label className="auth-field">
                <span>Mobile number (optional)</span>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+91 …"
                />
              </label>
            )}
            <label className="auth-field">
              <span>Password</span>
              <div className="pw-wrap">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  placeholder={mode === "login" ? "Your password" : "Min. 6 characters"}
                  minLength={6}
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            {mode === "login" && (
              <div className="auth-forgot-row">
                <Link
                  to="/forgot-password"
                  className="auth-forgot-link"
                  data-testid="auth-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}

            <button type="submit" className="btn btn-primary auth-submit" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 size={16} className="spin" /> Please wait…
                </>
              ) : (
                <>
                  {mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="auth-swap">
            {mode === "login" ? (
              <>
                New to Alankar?{" "}
                <button type="button" onClick={() => setMode("register")}>
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button type="button" onClick={() => setMode("login")}>
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
